# The sound layer hands a loaded buffer to the instances waiting for it —
# card 6aab65a36c29c3a6dc3bbbb7.
#
# THE DEFECT IS IN THE LIBRARY. createjs 1.0.0's AbstractPlugin does this when a
# sound finishes preloading (builds/1.0.0/createjs.js, _handlePreloadComplete):
#
#     for (var i = 0, l = this._soundInstances[src].length; i < l; i++) {
#       var item = this._soundInstances[src][i];
#       item.setPlaybackResource(this._audioSources[src]);
#       this._soundInstances[src] = null;
#     }
#
# Two things are wrong with it. `setPlaybackResource` was replaced by the
# `playbackResource` PROPERTY in 0.6.0 — AbstractSoundInstance defines it with
# Object.defineProperty and no method of that name survives — so the call throws
# `setPlaybackResource is not a function` for any instance that is waiting. And
# the array is nulled INSIDE its own loop, so even a working call would only
# ever reach the first instance.
#
# WHEN IT RUNS. Only when a sound is PLAYED BEFORE ITS FILE HAS FINISHED
# LOADING: that is what puts an instance in `_soundInstances[src]` for the load
# to hand a buffer to. Measured on QA elfeet 2026-09-17, Knights at 932x430:
# five `Failed to play enter [knight_move.mp3] (playState indicates failure)`
# lines — loopback_api's `play` retrying an enter sound whose file was still in
# flight — and then an uncaught page error out of soundjs. It was the only
# uncaught error in 46 game plays.
#
# WHY IT MATTERS BEYOND THE CONSOLE LINE. The throw aborts the handoff, so the
# instances waiting on that file never receive their buffer at all: a sound
# played early stays silent for the rest of the session. That is the path a real
# device takes when the first play is refused — iOS autoplay policy is the
# ordinary case, not an exotic one.
#
# WHY A PATCH AND NOT AN UPGRADE. createjs 1.0.0 is the last release; the bug is
# still in it. This replaces the one method, on the prototype every plugin
# inherits it from, with the same method written correctly.
#
# ORDERING IS LOAD-BEARING. `AbstractPlugin.register` captures
# `this._handlePreloadComplete` as the loader's complete listener, so a sound
# registered before this runs would keep the broken one. It is called at module
# scope from each shell's app-route — the webpack entry — which is before any
# game, avatar picker or instruction set can register a sound.
import createjs from 'createjs'

export patchPlaybackResourceHandoff = (cjs = createjs) ->
  plugin = cjs?.AbstractPlugin?.prototype
  return false unless typeof plugin?._handlePreloadComplete is 'function'
  return false if plugin._playbackResourceHandoffPatched

  plugin._handlePreloadComplete = (event) ->
    src = event?.target?.getItem?()?.src
    return unless src?
    @_audioSources[src] = event.result
    waiting = @_soundInstances?[src]
    if waiting?.length
      for item in waiting when item?
        try
          # The property, which is what 1.0.0 actually has. The method call is
          # kept as the first branch only so a future createjs that restores it
          # is handled by the same code.
          if typeof item.setPlaybackResource is 'function'
            item.setPlaybackResource @_audioSources[src]
          else
            item.playbackResource = @_audioSources[src]
        catch e
          # One instance that cannot take the buffer must not cost the others
          # theirs, and must never reach the page as an uncaught error.
          console.warn "Could not hand the loaded buffer to a sound instance for #{src}: #{e?.message}"
    # AFTER the loop, not inside it (see above).
    @_soundInstances[src] = null if @_soundInstances?
    return

  plugin._playbackResourceHandoffPatched = true
  true


# A clip that will not decode ends the say(), it does not crash the page —
# card 6ab1f98bcb41c3b4ffd4bd76.
#
# THE PRODUCTION CRASH. Fingerprint 127fdbbc, `EncodingError: Decoding failed`,
# unhandledrejection, no stack: 49 rows on 2026-09-21 from two accounts, every
# one the iOS app on /app#/play. The console tail is the same every time — the
# Piper greeting is found in the manifest, "iOS / mobile safari detected ::
# using soundjs for Piper", "soundjs :: registering sound: <md5>.mp3" — and the
# crash follows inside the same second.
#
# THE DEFECT IS IN THE LIBRARY, again. createjs 1.0.0's WebAudioLoader
# (builds/1.0.0/createjs.js, _sendComplete) decodes the clip like this:
#
#     Loader.context.decodeAudioData(this._rawResult,
#          createjs.proxy(this._handleAudioDecoded, this),
#          createjs.proxy(this._sendError, this));
#
# It DROPS the return value. WebKit's decodeAudioData honours the callback form
# AND returns a promise, so a clip that will not decode rejects a promise nobody
# is holding: an unhandled rejection, straight to window.onunhandledrejection
# and into CrashLog, once per attempt. That is the whole of 127fdbbc.
#
# AND THE CHILD HEARS NOTHING. WebKit hands the error callback a DOMException,
# which has no `type`, so `_sendError`'s dispatchEvent finds no listener for
# `undefined` and dispatches NOTHING: no `error`, so no `fileerror`, so
# instruction_set's playFailure never runs, so say() never completes and the
# game waits. The 33-rows-in-an-hour shape is a child replaying a greeting that
# can never finish.
#
# SO THE REPLACEMENT DOES TWO THINGS, and the second is the one the child
# notices: it keeps the promise and catches it, and it turns EVERY failure —
# callback or rejection, DOMException or null — into exactly one
# `createjs.ErrorEvent('DECODE_FAILED', ...)`. A real ErrorEvent has
# `type: 'error'`, so it reaches `fileerror`, so playFailure blacklists that md5
# for the session and says the next greeting variant instead
# (instruction_set.coffee:780-790). Nothing is deleted and no file is touched:
# the skip is in memory and lasts the session.
#
# EXACTLY ONE. The callback and the rejected promise both fire for the same
# failure, so an unlatched fix reports the clip broken twice — and the second
# one arrives after playFailure has already moved on to the next variant, where
# it would blacklist an md5 that never failed.
#
# WHY A PATCH AND NOT AN UPGRADE: as above. 1.0.0 is the last release.
export patchWebAudioDecodeRejection = (cjs = createjs) ->
  loader = cjs?.WebAudioLoader?.prototype
  return false unless typeof loader?._sendComplete is 'function'
  return false if loader._decodeRejectionPatched

  loader._sendComplete = (event) ->
    settled = false
    # BOTH PATHS HAND OVER TO THE APP, SO BOTH ARE GUARDED (card
    # 6ab402104f3ebf539a3823e0). `_sendError` dispatches `fileerror` and
    # playFailure runs underneath it; `_handleAudioDecoded` hands the buffer to
    # whoever was waiting. Either can throw, and where the throw lands depends
    # only on which of the two settlement paths got here first. The promise
    # path was wrapped and the CALLBACK path was not — so a listener that threw
    # escaped as an uncaught error whenever the callback won, which in a
    # browser is every time (measured in Chrome on elfeet, decodeAudioData on 8
    # garbage bytes: 'callback:error -> promise:rejected', 5 runs out of 5).
    # The patch was passing only in the order the browser never uses.
    #
    # Guarding the handover itself, once, covers both callers — there is no
    # longer a path into the app from here that a throw can escape by.
    handOver = (what, give) ->
      try
        give()
      catch e
        console.warn "Sound decode handling threw while #{what}: #{e?.message or e}"
      return
    decoded = (buffer) =>
      return if settled
      settled = true
      handOver 'handing over a decoded clip', => @_handleAudioDecoded buffer
    failed = (err) =>
      return if settled
      settled = true
      # The title is what `fileerror` readers see; the message is what the
      # console line carries. `err` is a DOMException on WebKit, an ErrorEvent
      # on some builds, and null when the browser says nothing at all.
      detail = err?.message or err?.title or (if err? then String(err) else 'the browser gave no reason')
      handOver 'reporting a decode failure', =>
        @_sendError new cjs.ErrorEvent('DECODE_FAILED', "Could not decode audio: #{detail}", err)

    context = cjs?.WebAudioLoader?.context
    unless context?.decodeAudioData?
      # No context to decode with is not something to throw about mid-crash;
      # it is a load that failed.
      return failed null

    try
      pending = context.decodeAudioData @_rawResult, decoded, failed
    catch e
      return failed e
    # THE LINE THE LIBRARY IS MISSING. Older WebKit returns undefined here and
    # only the callbacks fire; every current engine also returns a promise.
    #
    # The trailing catch is not decoration. `.then(...)` returns a NEW promise,
    # and anything that throws while handling the load would reject it with
    # nobody holding it: a fresh unhandled rejection, which is the exact thing
    # this patch exists to remove. `handOver` above already stops a listener's
    # throw reaching either settlement path; this stays as the outer net for
    # whatever else the chain might raise, and because the cost of it is a line.
    if typeof pending?.then is 'function'
      pending.then(decoded, failed).catch (e) ->
        console.warn "Sound decode handling threw: #{e?.message or e}"
    return

  loader._decodeRejectionPatched = true
  true
